import { useMemo } from "react";

import type { TabbedDropdownProps } from "@similarweb/ui-components";

import { DropdownSmartTabs } from "app/pages/sales-intelligence/common-components/dropdown/DropdownTabs/DropdownTabs";
import type { GroupedTechnologies } from "app/pages/sales-intelligence/domain/technology/types/model";
import useDelayedAction from "app/pages/sales-intelligence/hooks/useDelayedAction";
import type {
    TechnologiesBaseListItem,
    TechnologiesConditionEntry,
    TechnologiesDDItemType,
} from "app/pages/sales-intelligence/modules/technology/types/technologies";
import { getTechnologiesListItems } from "app/pages/sales-intelligence/modules/technology/utils/technologies";
import DropdownTabPanel from "app/pages/sales-intelligence/sub-modules/advanced-search/components/shared/TabbedDropdown/DropdownTabPanel/DropdownTabPanel";
import useTechnologiesSearch from "app/pages/sales-intelligence/sub-modules/advanced-search/hooks/useTechnologiesSearch";
import useTechnologiesTabs from "app/pages/sales-intelligence/sub-modules/advanced-search/hooks/useTechnologiesTabs";
import useToggleState from "app/pages/sales-intelligence/sub-modules/advanced-search/hooks/useToggleState";
import type { ExtendedTabbedDropdownProps } from "app/pages/sales-intelligence/types/types";

import {
    DROPDOWN_HEIGHT,
    StyledFilterTabbedDropdown as StyledTechnologiesDropdown,
    VERTICAL_OFFSET,
} from "../../shared/FilterTabbedDropdown/styles";
import { FILTER_AUTO_OPEN_DELAY } from "../utils/const";

type Props = Pick<
    TabbedDropdownProps<TechnologiesDDItemType>,
    "defaultActiveTab" | "isDisabled" | "buttonComponent" | "footerComponent"
> &
    ExtendedTabbedDropdownProps<TechnologiesConditionEntry, TechnologiesBaseListItem> & {
        options: Readonly<GroupedTechnologies>;
        onToggle?(isOpen: boolean): void;
    };

const FilterTechnologiesDropdown = (props: Props) => {
    const {
        options,
        onToggle,
        onSelect,
        onItemsSort,
        onNoResultsFound,
        searchPlaceholder,
        selectedEntries,
        buttonComponent: ButtonComponent,
        ...rest
    } = props;

    const handleToggle = (isOpen: boolean): void => {
        onToggle?.(isOpen);
    };

    const [isOpen, setIsOpen] = useToggleState(false, handleToggle);

    const { categories, subCategories, technologies } = useMemo(() => getTechnologiesListItems(options), [options]);

    const { sortedItems, searchTerm, searchProps } = useTechnologiesSearch(
        {
            categories,
            subCategories,
            technologies,
            onItemsSort,
            onNoResultsFound,
            searchPlaceholder,
        },
        { shouldProcessItems: isOpen },
    );

    const { tabs = [] } = useTechnologiesTabs({
        isOpen,
        disabledEntries: [],
        selectedEntries,
        sortedItems,
    });

    useDelayedAction(FILTER_AUTO_OPEN_DELAY, () => setIsOpen(true), !props.isDisabled);

    const handleItemClick = (item: TechnologiesDDItemType): void => {
        const { name, type } = item;
        onSelect({ name, type });
    };

    const renderButton = (buttonProps: Pick<TabbedDropdownProps<TechnologiesDDItemType>, "isOpen">) => (
        <ButtonComponent
            isOpen={isOpen}
            {...buttonProps}
        />
    );

    return (
        <StyledTechnologiesDropdown
            {...rest}
            buttonComponent={renderButton}
            tabs={tabs}
            isOpen={isOpen}
            onToggle={setIsOpen}
            searchProps={searchProps}
            searchTerm={searchTerm}
            onItemClick={handleItemClick}
            panelComponent={DropdownTabPanel}
            tabsView={DropdownSmartTabs}
            verticalOffset={VERTICAL_OFFSET}
            height={DROPDOWN_HEIGHT}
        />
    );
};

export default FilterTechnologiesDropdown;
